import {
    addExamplesToClause,
    deleteExamples,
    IClause,
    IClauseExample,
    IClauseExampleCreate,
    IClauseExampleUpdate,
    updateExample,
} from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { Panel } from "@/components/shared/Panel";
import { Add, Edit, Remove } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useFieldArray } from "react-hook-form";

interface IProps {
    clause: IClause;
}

interface IExamplesForm {
    examples: IClauseExample[];
}

/**
 * Section displaying clause examples with inline editing
 */
export const ClauseExamplesSection = ({ clause }: IProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const queryClient = useQueryClient();

    const { mutate: saveExamples, isPending: isSaving } = useMutation({
        mutationFn: async (formData: IExamplesForm) => {
            // Filter examples that are not yet created (id === -1)
            const examplesToCreate: IClauseExampleCreate = {
                examples: formData.examples
                    .filter(({ id }) => id === -1)
                    .map(({ text }) => text),
            };

            // Filter examples that need to be removed
            const examplesToRemove = clause.examples.filter(
                ({ id }) =>
                    !formData.examples.some(
                        ({ id: exampleId }) => exampleId === id
                    )
            );

            // Filter examples that need to be updated (Text has changed)
            const examplesToUpdate = formData.examples.filter(
                ({ id: formExampleId, text: formExampleText }) =>
                    clause.examples.some(
                        ({ id, text }) =>
                            formExampleId === id && formExampleText !== text
                    )
            );

            // Delete examples that need to be removed
            if (examplesToRemove.length > 0) {
                await deleteExamples(
                    clause.id,
                    examplesToRemove.map(({ id }) => id)
                );
            }

            // Create examples that are not yet created
            if (examplesToCreate.examples.length > 0) {
                await addExamplesToClause(clause.id, examplesToCreate);
            }

            // Update examples that need to be updated
            if (examplesToUpdate.length > 0) {
                for (const example of examplesToUpdate) {
                    const exampleUpdate: IClauseExampleUpdate = {
                        text: example.text,
                    };
                    await updateExample(clause.id, example.id, exampleUpdate);
                }
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-clause-details", String(clause.id)],
            });
            queryClient.invalidateQueries({ queryKey: ["admin-clauses"] });
            setIsEditing(false);
        },
    });

    const handleSave = (formData: IExamplesForm) => {
        saveExamples(formData);
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    const defaultValues: IExamplesForm = {
        examples: clause.examples
            .map(({ id, text }) => ({ id, text }))
            .sort((a, b) => a.id - b.id),
    };

    return (
        <Panel
            title={`Examples (${clause.examples.length})`}
            actions={
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit fontSize="small" />}
                    onClick={() => setIsEditing(true)}
                    sx={{
                        visibility: isEditing ? "hidden" : "visible",
                        color: (theme) => theme.palette.primary.contrastText,
                        borderColor: (theme) =>
                            theme.palette.primary.contrastText,
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.action.hover,
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                        },
                    }}
                >
                    Edit
                </Button>
            }
        >
            {isEditing ? (
                <FormContainer
                    defaultValues={defaultValues}
                    onSuccess={handleSave}
                >
                    <ExamplesEditForm
                        onCancel={handleCancel}
                        isSaving={isSaving}
                    />
                </FormContainer>
            ) : (
                <Stack spacing={2}>
                    {clause.examples.length > 0 ? (
                        clause.examples
                            .sort((a, b) => a.id - b.id)
                            .map((example, index) => (
                                <Box
                                    key={example.id}
                                    p={2}
                                    bgcolor="grey.50"
                                    borderRadius={1}
                                >
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        fontWeight={500}
                                    >
                                        Example {index + 1}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}
                                    >
                                        {example.text}
                                    </Typography>
                                </Box>
                            ))
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No examples added yet.
                        </Typography>
                    )}
                </Stack>
            )}
        </Panel>
    );
};

interface IExamplesEditFormProps {
    onCancel: () => void;
    isSaving: boolean;
}

const ExamplesEditForm = ({ onCancel, isSaving }: IExamplesEditFormProps) => {
    const { fields, append, remove } = useFieldArray({
        name: "examples",
    });

    return (
        <Stack spacing={2}>
            {fields.map((field, index) => (
                <Stack
                    key={field.id}
                    direction="row"
                    alignItems="flex-start"
                    gap={1}
                >
                    <FormTextFieldElement
                        label={`Example ${index + 1}`}
                        name={`examples.${index}.text`}
                        size="small"
                        multiline
                        minRows={2}
                        fullWidth
                        required
                    />
                    {fields.length > 1 && (
                        <IconButton
                            onClick={() => remove(index)}
                            aria-label="Remove example"
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            <Remove />
                        </IconButton>
                    )}
                </Stack>
            ))}

            <Box>
                <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<Add fontSize="small" />}
                    onClick={() =>
                        append({ id: -1, text: "" } as IClauseExample)
                    }
                    sx={{ borderRadius: 2 }}
                >
                    Add Example
                </Button>
            </Box>

            <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={onCancel}
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    loading={isSaving}
                >
                    Save
                </Button>
            </Stack>
        </Stack>
    );
};
